import React, { useEffect, useState } from "react";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Input from "@material-ui/core/Input";
import CircularProgress from "@material-ui/core/CircularProgress";

import { api } from "./api";
import SelectorCreativo from "./SelectorCreativo";
import RepartirPaises from "./RepartirPaises";
import SelectorIntereses from "./SelectorIntereses";
import { Bar, Btn, Lbl, Panel, Strip } from "./ui";

const OBJETIVOS = ["OUTCOME_SALES", "OUTCOME_LEADS", "OUTCOME_TRAFFIC", "OUTCOME_ENGAGEMENT", "OUTCOME_AWARENESS"];
const OPTIMIZACIONES = ["OFFSITE_CONVERSIONS", "LANDING_PAGE_VIEWS", "LINK_CLICKS", "THRUPLAY", "REACH", "IMPRESSIONS"];
const CTAS = ["LEARN_MORE", "SHOP_NOW", "SIGN_UP", "SUBSCRIBE", "GET_OFFER", "DOWNLOAD"];

/** Fila de campos en grilla: más compacta que el GridContainer del tema, que trae gutters de 15px. */
const Fila = ({ cols, children, style }) => (
  <div style={{ display: "grid", gridTemplateColumns: cols, gap: 8, alignItems: "end", ...style }}>{children}</div>
);

/**
 * Armado y ajuste de una campaña antes de publicarla.
 *
 * El flujo es: pedir una idea en texto → el generador propone una estructura → se ajusta todo a
 * mano (creativos, países, públicos, presupuesto, copy) → se propone. Proponer NO publica: deja
 * una acción pendiente de aprobación, y todo lo que se cree en Meta nace pausado.
 */
export default function ArmarCampana({ onIrAAprobaciones }) {
  const [catalogo, setCatalogo] = useState(null);
  const [idea, setIdea] = useState("");
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(null);

  const [borrador, setBorrador] = useState(null);
  const [estructura, setEstructura] = useState(null);
  const [errores, setErrores] = useState([]);
  const [advertencias, setAdvertencias] = useState([]);
  const [razonamiento, setRazonamiento] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const [lista, setLista] = useState([]);
  const [verLista, setVerLista] = useState(true);
  const [selector, setSelector] = useState(null); // {adset, anuncio}
  const [repartir, setRepartir] = useState(null); // índice del conjunto plantilla
  const [quien, setQuien] = useState("");
  const [verIdea, setVerIdea] = useState(true);
  const [verRazon, setVerRazon] = useState(false);

  useEffect(() => {
    api.catalogo(90).then(setCatalogo).catch((e) => setError(e.message));
    refrescarLista();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Los borradores viven en la DB, no en el estado de React. Sin esta lista, guardar y recargar
   * dejaba el borrador invisible aunque estuviera guardado: no había por dónde volver a él.
   */
  function refrescarLista() {
    api.listarBorradores().then((r) => setLista(r.borradores || [])).catch(() => setLista([]));
  }

  async function abrir(id) {
    setError(null);
    setMensaje(null);
    try {
      const r = await api.verBorrador(id);
      setBorrador(r.borrador);
      setEstructura(r.borrador.estructura);
      setRazonamiento(r.borrador.razonamiento || "");
      setValidacion(r.validacion);
      setVerIdea(false);
      setVerLista(false);
    } catch (e) {
      setError(e.message);
    }
  }

  async function descartar(id) {
    try {
      await api.descartarBorrador(id);
      if (borrador && borrador.id === id) {
        setBorrador(null);
        setEstructura(null);
      }
      refrescarLista();
      setMensaje(`Borrador #${id} descartado.`);
    } catch (e) {
      setError(e.message);
    }
  }

  /*
   * La API devuelve {errores, advertencias} y la distinción importa: sólo los errores impiden
   * proponer. Repartir el anuncio en un conjunto por país avisa que fragmenta el aprendizaje,
   * y eso es una decisión legítima — si bloqueara, ese caso sería imposible de publicar.
   */
  const setValidacion = (v) => {
    setErrores((v && v.errores) || []);
    setAdvertencias((v && v.advertencias) || []);
  };

  const editable = !borrador || borrador.estado === "borrador";

  async function generar() {
    if (!idea.trim()) return;
    setGenerando(true);
    setError(null);
    setMensaje(null);
    try {
      const r = await api.generarBorrador({ idea, por: quien || undefined });
      setBorrador(r.borrador);
      setEstructura(r.borrador.estructura);
      setValidacion(r.validacion);
      setRazonamiento(r.borrador.razonamiento || "");
      setVerIdea(false);   // ya no hace falta el textarea ocupando pantalla
      refrescarLista();
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerando(false);
    }
  }

  async function empezarVacio() {
    setError(null);
    setMensaje(null);
    try {
      const r = await api.crearBorrador({ nombre: "Campaña nueva", por: quien || undefined });
      setBorrador(r.borrador);
      setEstructura(r.borrador.estructura);
      setValidacion(r.validacion);
      setRazonamiento("");
      setVerIdea(false);
      refrescarLista();
    } catch (e) {
      setError(e.message);
    }
  }

  async function guardar() {
    if (!borrador) return;
    setGuardando(true);
    setError(null);
    try {
      const r = await api.guardarBorrador(borrador.id, { estructura, nombre: estructura.campana.nombre });
      setBorrador(r.borrador);
      setValidacion(r.validacion);
      setMensaje("Cambios guardados.");
      refrescarLista();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  }

  async function proponer() {
    if (!borrador) return;
    if (!quien.trim()) {
      setError("Poné tu nombre: toda propuesta queda registrada con su responsable.");
      return;
    }
    setError(null);
    try {
      await api.guardarBorrador(borrador.id, { estructura, nombre: estructura.campana.nombre });
      const r = await api.proponerBorrador(borrador.id, { por: quien });
      setBorrador(r.borrador);
      setMensaje("Propuesta enviada a aprobación. No se creó nada en Meta: hay que aprobarla y después correr meta:apply.");
      refrescarLista();
    } catch (e) {
      setError(e.message);
      if (e.datos && e.datos.validacion) setValidacion(e.datos.validacion);
    }
  }

  const set = (fn) => {
    const copia = JSON.parse(JSON.stringify(estructura));
    fn(copia);
    setEstructura(copia);
  };

  const agregarAdset = () =>
    set((e) =>
      e.adsets.push({
        nombre: `Conjunto ${e.adsets.length + 1}`, paises: [], edad_min: 18, edad_max: 65,
        generos: "todos", audiencias_incluir: [], audiencias_excluir: [], intereses: [],
        optimizacion: "OFFSITE_CONVERSIONS", evento: "INITIATE_CHECKOUT",
        presupuesto_diario: 0, anuncios: [],
      })
    );

  const agregarAnuncio = (i) =>
    set((e) =>
      e.adsets[i].anuncios.push({
        nombre: `Anuncio ${e.adsets[i].anuncios.length + 1}`, creativo_clave: "",
        texto_primario: "", titulo: "", descripcion: "", cta: "LEARN_MORE",
      })
    );

  const quitarAdset = (i) => set((e) => e.adsets.splice(i, 1));
  const quitarAnuncio = (i, j) => set((e) => e.adsets[i].anuncios.splice(j, 1));

  function creativo(clave) {
    return ((catalogo && catalogo.creativos) || []).find((x) => x.clave === clave);
  }

  if (error && !catalogo) return <Strip tone="error">{error}</Strip>;
  if (!catalogo) return <CircularProgress size={22} />;

  const cap = catalogo.aprendizaje || {};

  const ESTADO_COLOR = { borrador: "#CA8A04", propuesto: "#0369A1", publicado: "#16A34A", descartado: "#94A3B8" };

  return (
    <div>
      {/* ---------- borradores guardados ---------- */}
      {lista.length > 0 && (
        <Panel
          title={`Borradores guardados (${lista.length})`}
          right={<Btn onClick={() => setVerLista(!verLista)}>{verLista ? "ocultar" : "ver"}</Btn>}
          pad={0}
        >
          {verLista && (
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>campaña</th>
                  <th style={{ textAlign: "left" }}>estado</th>
                  <th style={{ textAlign: "right" }}>conj.</th>
                  <th style={{ textAlign: "right" }}>anun.</th>
                  <th style={{ textAlign: "right" }}>USD/día</th>
                  <th style={{ textAlign: "left" }}>creado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lista.map((b) => (
                  <tr key={b.id} style={{ background: borrador && borrador.id === b.id ? "#F4F8FF" : undefined }}>
                    <td>
                      <div style={{ fontWeight: 600 }}>#{b.id} {b.nombre}</div>
                      {b.idea && (
                        <div style={{ fontSize: 10.5, color: "#A0AABA", maxWidth: 420, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {b.idea}
                        </div>
                      )}
                    </td>
                    <td style={{ color: ESTADO_COLOR[b.estado] || "#24303F", fontWeight: 700, fontSize: 11 }}>
                      {b.estado}
                    </td>
                    <td style={{ textAlign: "right" }}>{b.resumen.adsets}</td>
                    <td style={{ textAlign: "right" }}>{b.resumen.anuncios}</td>
                    <td style={{ textAlign: "right" }}>${b.resumen.presupuesto_diario}</td>
                    <td style={{ fontSize: 11, color: "#7A8699" }}>
                      {(b.creado_at || "").substring(0, 10)}
                      {b.generado_por === "llm" ? " · generado" : " · a mano"}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <Btn tone="pri" onClick={() => abrir(b.id)}>abrir</Btn>{" "}
                      {b.estado === "borrador" && (
                        <Btn tone="no" onClick={() => descartar(b.id)}>descartar</Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {/* ---------- pedir la idea ---------- */}
      <Panel
        title="Armar una campaña"
        right={
          borrador ? (
            <Btn onClick={() => setVerIdea(!verIdea)}>{verIdea ? "ocultar" : "cambiar idea"}</Btn>
          ) : null
        }
      >
        {verIdea && (
          <>
            <TextField
              placeholder="Describí la idea o la estrategia. Ej: probar tutoriales cortos en México y Colombia con presupuesto chico, apuntando a gente que ya vio videos nuestros"
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              multiline
              rows={2}
              fullWidth
              variant="outlined"
            />
            <Bar style={{ marginTop: 8, marginBottom: 0 }}>
              <TextField placeholder="tu nombre" value={quien} onChange={(e) => setQuien(e.target.value)} style={{ width: 140 }} />
              <Btn tone="pri" onClick={generar} disabled={generando || !idea.trim()}>
                {generando ? "generando…" : "generar propuesta"}
              </Btn>
              <Btn onClick={empezarVacio} disabled={generando}>armar a mano</Btn>
              <span style={{ fontSize: 11.5, color: "#7A8699" }}>
                {catalogo.creativos.length} creativos · {catalogo.paises.length} países · {catalogo.audiencias.length} públicos ·
                volumen sostiene <b>{cap.capacidad}</b> conjunto(s)
              </span>
            </Bar>
          </>
        )}
        {!verIdea && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 12.5 }}>
            <TextField placeholder="tu nombre" value={quien} onChange={(e) => setQuien(e.target.value)} style={{ width: 140 }} />
            <span style={{ color: "#7A8699", flex: 1, minWidth: 200 }}>
              {borrador && borrador.idea ? `«${borrador.idea.substring(0, 120)}${borrador.idea.length > 120 ? "…" : ""}»` : "borrador armado a mano"}
            </span>
            {razonamiento && <Btn onClick={() => setVerRazon(!verRazon)}>{verRazon ? "ocultar por qué" : "por qué se propuso"}</Btn>}
          </div>
        )}
      </Panel>

      {error && <Strip tone="error">{error}</Strip>}
      {mensaje && <Strip tone="ok">{mensaje}</Strip>}
      {verRazon && razonamiento && <Strip tone="info" style={{ whiteSpace: "pre-wrap" }}>{razonamiento}</Strip>}

      {errores.length > 0 && (
        <Strip tone="error">
          <b>Corregir antes de poder proponer:</b>{" "}
          {errores.map((a, i) => (
            <span key={i}>{i > 0 ? " · " : ""}{a}</span>
          ))}
        </Strip>
      )}

      {/* Las advertencias informan y no bloquean: son decisiones, no defectos. */}
      {advertencias.length > 0 && (
        <Strip tone="warn">
          <b>A tener en cuenta:</b>{" "}
          {advertencias.map((a, i) => (
            <span key={i}>{i > 0 ? " · " : ""}{a}</span>
          ))}
        </Strip>
      )}

      {estructura && (
        <>
          {/* ---------- campaña ---------- */}
          <Panel
            title={`Campaña${borrador ? ` · borrador #${borrador.id} (${borrador.estado})` : ""}`}
          >
            <Fila cols="2fr 1.3fr 0.9fr">
              <div>
                <Lbl>nombre</Lbl>
                <TextField value={estructura.campana.nombre} onChange={(e) => set((x) => (x.campana.nombre = e.target.value))} fullWidth disabled={!editable} />
              </div>
              <div>
                <Lbl>objetivo</Lbl>
                <Select value={estructura.campana.objetivo} onChange={(e) => set((x) => (x.campana.objetivo = e.target.value))} fullWidth disabled={!editable}>
                  {OBJETIVOS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </div>
              <div>
                <Lbl>presup. campaña USD/día</Lbl>
                <TextField type="number" value={estructura.campana.presupuesto_diario} onChange={(e) => set((x) => (x.campana.presupuesto_diario = Number(e.target.value)))} fullWidth disabled={!editable} />
              </div>
            </Fila>
            <div style={{ fontSize: 11, color: "#A0AABA", marginTop: 3 }}>0 = el presupuesto va por conjunto</div>

            {/*
              Destino. Antes era una constante global del .env, así que TODAS las campañas
              apuntaban al mismo lado. Vacío sigue usando ese valor global.
            */}
            <Fila cols="1fr" style={{ marginTop: 10 }}>
              <div>
                <Lbl>destino de los anuncios</Lbl>
                <TextField
                  value={estructura.campana.destino || ""}
                  onChange={(e) => set((x) => (x.campana.destino = e.target.value))}
                  placeholder={(catalogo && catalogo.destino_default) || "https://www.tusclasesdeguitarra.com/..."}
                  fullWidth
                  disabled={!editable}
                />
                <div style={{ fontSize: 11, color: "#A0AABA", marginTop: 3 }}>
                  Vacío usa el destino global{catalogo && catalogo.destino_default ? ` (${catalogo.destino_default})` : ""}.
                  Los utm los agrega Meta sola con los ids de campaña, conjunto y anuncio.
                </div>
              </div>
            </Fila>
          </Panel>

          {/* ---------- conjuntos ---------- */}
          {estructura.adsets.map((as, i) => (
            <Panel
              key={i}
              title={`Conjunto ${i + 1}`}
              right={
                <>
                  <Btn onClick={() => setRepartir(i)} disabled={!editable} title="Publicar este mismo anuncio en varios países">
                    varios países
                  </Btn>
                  <Btn onClick={() => agregarAnuncio(i)} disabled={!editable}>+ anuncio</Btn>
                  {estructura.adsets.length > 1 && (
                    <Btn tone="no" onClick={() => quitarAdset(i)} disabled={!editable}>quitar</Btn>
                  )}
                </>
              }
            >
              {/*
                Dos filas y no una: con los nueve campos en una sola grilla cada columna quedaba
                en ~110px, las etiquetas largas se partían en tres renglones y "evento a
                optimizar" pasaba desapercibido. Arriba va el QUÉ y CUÁNTO; abajo, a quién y a
                qué se optimiza.
              */}
              <Fila cols="1.4fr 1.4fr 0.5fr 0.5fr 0.7fr">
                <div>
                  <Lbl>nombre</Lbl>
                  <TextField value={as.nombre} onChange={(e) => set((x) => (x.adsets[i].nombre = e.target.value))} fullWidth disabled={!editable} />
                </div>
                <div>
                  <Lbl>países</Lbl>
                  <Select
                    multiple
                    value={as.paises}
                    onChange={(e) => set((x) => (x.adsets[i].paises = e.target.value))}
                    input={<Input />}
                    renderValue={(sel) => sel.join(", ") || "—"}
                    fullWidth
                    disabled={!editable}
                  >
                    {catalogo.paises.map((p) => <MenuItem key={p.iso} value={p.iso}>{p.iso} — {p.nombre}</MenuItem>)}
                  </Select>
                </div>
                <div>
                  <Lbl>edad</Lbl>
                  <TextField type="number" value={as.edad_min} onChange={(e) => set((x) => (x.adsets[i].edad_min = Number(e.target.value)))} fullWidth disabled={!editable} />
                </div>
                <div>
                  <Lbl>a</Lbl>
                  <TextField type="number" value={as.edad_max} onChange={(e) => set((x) => (x.adsets[i].edad_max = Number(e.target.value)))} fullWidth disabled={!editable} />
                </div>
                <div>
                  <Lbl>USD/día</Lbl>
                  <TextField type="number" value={as.presupuesto_diario} onChange={(e) => set((x) => (x.adsets[i].presupuesto_diario = Number(e.target.value)))} fullWidth disabled={!editable} />
                </div>
              </Fila>

              <Fila cols="1fr 1fr 1.2fr 1.2fr" style={{ marginTop: 8 }}>
                <div>
                  <Lbl>optimización</Lbl>
                  <Select value={as.optimizacion} onChange={(e) => set((x) => (x.adsets[i].optimizacion = e.target.value))} fullWidth disabled={!editable}>
                    {OPTIMIZACIONES.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                </div>
                {/*
                  Evento del píxel. Antes iba PURCHASE fijo y era el motivo por el que toda
                  campaña nacía trabada en aprendizaje. Sólo aplica con OFFSITE_CONVERSIONS.

                  El vacío es una opción visible y no un valor pintado: antes, cuando el generador
                  no mandaba `evento`, el select mostraba PURCHASE como si estuviera elegido y no
                  había forma de notar que en la estructura no había nada guardado.
                */}
                <div>
                  <Lbl>evento a optimizar</Lbl>
                  <Select
                    value={as.evento || ""}
                    onChange={(e) => set((x) => (x.adsets[i].evento = e.target.value))}
                    fullWidth
                    displayEmpty
                    disabled={!editable || as.optimizacion !== "OFFSITE_CONVERSIONS"}
                  >
                    <MenuItem value="">
                      sin elegir → {(catalogo && catalogo.evento_default) || "PURCHASE"}
                    </MenuItem>
                    {((catalogo && catalogo.eventos) || ["PURCHASE"]).map((ev) => (
                      <MenuItem key={ev} value={ev}>{ev}</MenuItem>
                    ))}
                  </Select>
                  {as.optimizacion !== "OFFSITE_CONVERSIONS" && (
                    <div style={{ fontSize: 10.5, color: "#A0AABA", marginTop: 2 }}>
                      sólo aplica optimizando a conversiones
                    </div>
                  )}
                </div>
                <div>
                  <Lbl>incluir públicos (remarketing)</Lbl>
                  <Select
                    multiple
                    value={as.audiencias_incluir || []}
                    onChange={(e) => set((x) => (x.adsets[i].audiencias_incluir = e.target.value))}
                    input={<Input />}
                    renderValue={(sel) => (sel.length ? `${sel.length} público(s)` : "—")}
                    fullWidth
                    disabled={!editable}
                  >
                    {catalogo.audiencias.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.nombre}{a.tamano ? ` · ${Number(a.tamano).toLocaleString("es-AR")}` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                <div>
                  <Lbl>excluir públicos</Lbl>
                  <Select
                    multiple
                    value={as.audiencias_excluir || []}
                    onChange={(e) => set((x) => (x.adsets[i].audiencias_excluir = e.target.value))}
                    input={<Input />}
                    renderValue={(sel) => (sel.length ? `${sel.length} público(s)` : "—")}
                    fullWidth
                    disabled={!editable}
                  >
                    {catalogo.audiencias.map((a) => <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>)}
                  </Select>
                </div>
              </Fila>

              <SelectorIntereses
                valor={as.intereses}
                disabled={!editable}
                onCambio={(v) => set((x) => (x.adsets[i].intereses = v))}
              />

              {/* anuncios */}
              {as.anuncios.map((an, j) => {
                const c = creativo(an.creativo_clave);
                return (
                  <div key={j} style={{ borderTop: "1px solid #EEF1F6", marginTop: 8, paddingTop: 8 }}>
                    <Fila cols="1.1fr 2.2fr 0.9fr" style={{ marginBottom: 6 }}>
                      <div>
                        <Lbl>anuncio {j + 1}</Lbl>
                        <TextField value={an.nombre} onChange={(e) => set((x) => (x.adsets[i].anuncios[j].nombre = e.target.value))} fullWidth disabled={!editable} />
                      </div>
                      <div>
                        <Lbl>creativo</Lbl>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 26 }}>
                          <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: c ? "#24303F" : "#B42318" }}>
                            {c
                              ? `${c.nombre} · ${c.tipo}${c.sin_historia ? " · sin historia" : ` · ROAS ${Number(c.roas).toFixed(2)} · CPA ${c.cpa == null ? "—" : "$" + Math.round(c.cpa)}`}`
                              : an.creativo_clave || "(sin elegir)"}
                          </span>
                          <Btn tone="pri" onClick={() => setSelector({ adset: i, anuncio: j })} disabled={!editable}>
                            {an.creativo_clave ? "reemplazar" : "elegir"}
                          </Btn>
                        </div>
                      </div>
                      <div>
                        <Lbl>botón</Lbl>
                        <Select value={an.cta} onChange={(e) => set((x) => (x.adsets[i].anuncios[j].cta = e.target.value))} fullWidth disabled={!editable}>
                          {CTAS.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                        </Select>
                      </div>
                    </Fila>

                    <Fila cols="2.4fr 1.2fr 1.2fr 0.4fr">
                      <div>
                        <Lbl>texto principal</Lbl>
                        <TextField value={an.texto_primario} onChange={(e) => set((x) => (x.adsets[i].anuncios[j].texto_primario = e.target.value))} fullWidth disabled={!editable} />
                      </div>
                      <div>
                        <Lbl>título</Lbl>
                        <TextField value={an.titulo} onChange={(e) => set((x) => (x.adsets[i].anuncios[j].titulo = e.target.value))} fullWidth disabled={!editable} />
                      </div>
                      <div>
                        <Lbl>descripción</Lbl>
                        <TextField value={an.descripcion} onChange={(e) => set((x) => (x.adsets[i].anuncios[j].descripcion = e.target.value))} fullWidth disabled={!editable} />
                      </div>
                      <div>
                        <Btn tone="no" onClick={() => quitarAnuncio(i, j)} disabled={!editable}>quitar</Btn>
                      </div>
                    </Fila>
                  </div>
                );
              })}

              {as.anuncios.length === 0 && (
                <div style={{ fontSize: 12, color: "#B42318", marginTop: 8 }}>Sin anuncios. Agregá al menos uno.</div>
              )}
            </Panel>
          ))}

          <Bar>
            <Btn onClick={agregarAdset} disabled={!editable}>+ conjunto</Btn>
            <Btn onClick={guardar} disabled={!editable || guardando}>{guardando ? "guardando…" : "guardar cambios"}</Btn>
            <Btn tone="ok" onClick={proponer} disabled={!editable || errores.length > 0} title={errores.length > 0 ? "Corregí los errores primero" : ""}>
              enviar a aprobación
            </Btn>
            {borrador && borrador.estado === "propuesto" && (
              <Btn onClick={onIrAAprobaciones}>ver aprobaciones →</Btn>
            )}
            <span style={{ fontSize: 11.5, color: "#7A8699" }}>
              Enviar a aprobación no publica nada. Todo lo que se cree en Meta nace pausado.
            </span>
          </Bar>
        </>
      )}

      {/*
        Reparto por país: reemplaza el conjunto plantilla por los que salgan, así "un conjunto
        por país" y "un conjunto con todos" son la misma operación con distinto resultado.
      */}
      <RepartirPaises
        abierto={repartir !== null}
        paises={catalogo.paises}
        plantilla={repartir !== null && estructura ? estructura.adsets[repartir] : null}
        onCerrar={() => setRepartir(null)}
        onAplicar={(nuevos) => {
          set((x) => x.adsets.splice(repartir, 1, ...nuevos));
          setRepartir(null);
          setMensaje(
            nuevos.length === 1
              ? `Un conjunto con ${nuevos[0].paises.length} país(es) y presupuesto unificado.`
              : `${nuevos.length} conjuntos, uno por país, cada uno con su presupuesto. Guardá para validar.`
          );
        }}
      />

      <SelectorCreativo
        abierto={!!selector}
        creativos={catalogo.creativos}
        onCerrar={() => setSelector(null)}
        onElegir={(c) => {
          set((x) => (x.adsets[selector.adset].anuncios[selector.anuncio].creativo_clave = c.clave));
          setSelector(null);
        }}
      />
    </div>
  );
}
